import React, { useState, useEffect, ChangeEvent } from 'react'
import { InputGroup, MenuItem, Menu, Classes } from '@blueprintjs/core'
import { Icon, Button, Flex, HighlightedText, Square } from '@ui'
import { navigate } from '@reach/router'
import { pluralize } from '@utils'
import useWorkplaceGroupsCatalog from '../hooks/useWorkplaceGroupsCatalog'
import { useShouldReload } from '@hooks/reloadContext'
import css from './workplaceGroupCatalog.module.scss'

export default function WorkplaceGroupCatalog() {
  const [search, setSearch] = useState('')
  const [empty, setEmpty] = useState(false)

  const workplaceGroups = useWorkplaceGroupsCatalog()
  const shouldReload = useShouldReload()

  useEffect(() => {
    workplaceGroups.reload()
    // eslint-disable-next-line
  }, [shouldReload.workplaceGroups])

  const filtered = search
    ? workplaceGroups.items.filter((i) => new RegExp(search, 'i').test(i.name))
    : workplaceGroups.items

  useEffect(() => {
    if (!workplaceGroups.isFetching && !filtered.length && !empty) {
      setEmpty(true)
    }

    if (!workplaceGroups.isFetching && filtered.length && empty) {
      setEmpty(false)
    }
  }, [filtered, empty, workplaceGroups.isFetching])

  if (empty) return <Empty />

  return (
    <div className={css.catalog}>
      <div className={css.catalog_input_container}>
        <InputGroup
          fill
          value={search}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
          data-input="manage_workplace_group_search"
          placeholder="Search..."
          leftIcon={<Icon name="search" />}
        />
      </div>
      <div>
        {workplaceGroups.isFetching && <Skeleton countLines={1} withSquare={false} />}
        {!workplaceGroups.isFetching && (
          <div className={css.catalog_results_title}>
            {filtered.length} {pluralize(filtered.length, 'record')}
          </div>
        )}
        <Menu>
          {workplaceGroups.isFetching && <Skeleton />}
          {!workplaceGroups.isFetching && (
            <>
              <MenuItem
                key="add_item"
                onClick={() => navigate('?newItem=workplaceGroup')}
                text={
                  <Flex
                    align="center"
                    data-button="new_workplace_group"
                    style={{ color: 'var(--primary-color)', width: '100%' }}
                  >
                    <Icon name="plus" style={{ paddingRight: 8 }} />
                    New Workplace Group
                  </Flex>
                }
              />
              {filtered?.map((wpg) => (
                <MenuItem
                  key={wpg.id}
                  onClick={() => navigate(`?workplaceGroupId=${wpg.id}`)}
                  text={
                    <WorkplaceItem
                      name={wpg.name}
                      color={wpg.color}
                      search={search}
                      employeesCount={wpg.totals.employees}
                      spacesCount={wpg.totals.spaces}
                    />
                  }
                />
              ))}
            </>
          )}
        </Menu>
      </div>
    </div>
  )
}

function Empty() {
  return (
    <div className={css.catalog}>
      <div className={css.catalog_icon_wrapper}>
        <Icon duotone name="construction" style={{ fontSize: 34, color: '#ff5722' }} />
      </div>
      <div className={css.catalog_label}>
        <strong>No workplace groups</strong>
        <Button
          data-button="new_workplace_group"
          iconName="plus"
          minimal
          blue
          text="New Workplace Group"
          fill={false}
          onClick={() => {
            navigate('?newItem=workplaceGroup')
          }}
        />
      </div>
    </div>
  )
}

export function WorkplaceItem({
  name,
  color,
  search,
  employeesCount,
  spacesCount,
}: {
  name: string
  color: string
  search?: string
  employeesCount?: number
  spacesCount?: number
}) {
  return (
    <Flex data-item={name} align="center">
      <Square style={{ backgroundColor: `#${color}` }} />
      <div>
        <div
          className={css.catalog_name}
          style={{ lineHeight: '15px', fontSize: 14, color: 'var(--black)' }}
        >
          <HighlightedText text={name} query={search} />
          {employeesCount && spacesCount && (
            <span style={{ fontSize: 10, color: 'var(--grey1)' }}>
              {`${employeesCount} ${pluralize(
                employeesCount,
                'employee'
              )} | ${spacesCount} ${pluralize(spacesCount, 'space')}`}
            </span>
          )}
        </div>
      </div>
    </Flex>
  )
}

function Skeleton({
  countLines = 6,
  withSquare = true,
}: {
  countLines?: number
  withSquare?: boolean
}) {
  function Placeholder() {
    return (
      <Flex align="center" style={{ padding: '0 7px', height: 40, width: 280 }}>
        {withSquare && (
          <div
            style={{
              width: 14,
              height: 14,
              marginRight: 10,
            }}
            className={Classes.SKELETON}
          >
            Placeholder text
          </div>
        )}
        <div
          style={{
            width: 150,
            height: 16,
          }}
          className={Classes.SKELETON}
        >
          Placeholder text
        </div>
      </Flex>
    )
  }

  return (
    <>
      {Array.from({ length: countLines }, (_, k) => (
        <Placeholder key={k} />
      ))}
    </>
  )
}
